import { z } from "zod";

import type { SendMode } from "@/features/BroadcastV2/store/broadcastComposeStoreFactory";
import type {
  AbTestConfig,
  TestDurationUnit,
  VariationDraft,
  WinningMetric,
} from "@/features/BroadcastV2/store/variationTypes";
import type { ProductGridModelValue } from "@/features/EmailEditor/plugins/productGridConstants";
import type { TextComposerPrimaryChannel } from "@/features/TextComposerV2/channel/channelDerivation";
import type { TextComposerFramePreviewOutput } from "@/features/TextComposerV2/frame/frameOutput";
import type { CreateFramePayloadParsed } from "@/frames/schemas";

import { diffAudienceIds, diffVariations } from "@/api/broadcastV2Audiences";
import {
  DEFAULT_AB_TEST_CONFIG,
  MAX_VARIATIONS,
  variationLabel,
} from "@/features/BroadcastV2/store/variationTypes";
import {
  BROADCAST_NAME_MAX_LENGTH,
  DEFAULT_REACH_OPTIMIZATION_PERCENTAGE,
  createBroadcastRequestV2Schema,
  createReachOptimizationPercentageSchema,
  type AbTestOption,
  type AudiencesBroadcast,
  type BroadcastSchedule,
  type BroadcastVariation,
  type CreateBroadcastRequestV2,
  type UpdateBroadcastRequestV2,
  type WinningMetricWire,
} from "@/features/BroadcastV2/types";
import { buildTextComposerFrameVariantWireRows } from "@/features/TextComposerV2/frame/buildFrameUpdatePayload";
import { createFrameMetadata, createFramePayload } from "@/frames/service";

// --- Winning metric mapping (internal ↔ wire) ---

const WINNING_METRIC_TO_WIRE: Record<WinningMetric, WinningMetricWire> = {
  open_rate: "AB_TEST_METRIC_OPEN_RATE",
  click_rate: "AB_TEST_METRIC_CLICK_RATE",
  placed_order_rate: "AB_TEST_METRIC_ORDER_PLACED_RATE",
};

const WINNING_METRIC_FROM_WIRE: Record<WinningMetricWire, WinningMetric> = {
  AB_TEST_METRIC_OPEN_RATE: "open_rate",
  AB_TEST_METRIC_CLICK_RATE: "click_rate",
  AB_TEST_METRIC_ORDER_PLACED_RATE: "placed_order_rate",
};

export function winningMetricToWire(metric: WinningMetric): WinningMetricWire {
  return WINNING_METRIC_TO_WIRE[metric];
}

export function winningMetricFromWire(wire: WinningMetricWire): WinningMetric {
  return WINNING_METRIC_FROM_WIRE[wire];
}

// --- A/B test option: store ↔ wire ---

export function parseAbTestOptionFromWire(wire: AbTestOption): {
  winningMetric: WinningMetric;
  testSizePercent: number;
  testDurationHours: number;
} {
  return {
    winningMetric: winningMetricFromWire(wire.winningMetric),
    testSizePercent: Number(wire.testSizePercentage),
    testDurationHours: Number(wire.testDurationHours),
  };
}

/**
 * A broadcast is A/B only when it carries the option AND still has ≥2
 * variations. Disabling A/B removes the extra variation(s) but the patch-style
 * UpdateBroadcast API has no way to clear the persisted `abTestOption`, so a
 * lingering option on a single-variation broadcast must be treated as not A/B.
 */
export function isAbTestBroadcast(broadcast: AudiencesBroadcast): boolean {
  return (
    Boolean(broadcast.abTestOption) && (broadcast.variations?.length ?? 0) >= 2
  );
}

/**
 * Hydrate the store's A/B config from a broadcast's wire `abTestOption`. A/B
 * config is only present for multi-variation broadcasts; otherwise defaults are
 * returned. Shared by the email and SMS edit pages.
 */
export function hydrateAbConfig(broadcast: AudiencesBroadcast): AbTestConfig {
  if (!isAbTestBroadcast(broadcast)) {
    return { ...DEFAULT_AB_TEST_CONFIG };
  }
  const parsed = parseAbTestOptionFromWire(broadcast.abTestOption!);
  const { value: testDurationValue, unit: testDurationUnit } =
    convertTestDurationFromHours(parsed.testDurationHours);
  return {
    winningMetric: parsed.winningMetric,
    testSizePercent: parsed.testSizePercent,
    testDurationValue,
    testDurationUnit,
  };
}

/**
 * Hydrate the Smart Audience toggle + percentage from a broadcast's wire fields.
 * The toggle is on when a positive percentage is present OR the legacy bool is
 * set; the slider shows that percentage, or the default for a legacy bool-only
 * broadcast (which historically trimmed a fixed 20%). Shared by the email and
 * SMS edit pages.
 */
export function hydrateReachOptimization(broadcast: AudiencesBroadcast): {
  reachOptimization: boolean;
  reachOptimizationPercentage: number;
} {
  const parsed = broadcast.reachOptimizationPercentage
    ? Number(broadcast.reachOptimizationPercentage)
    : undefined;
  const percentage = parsed !== undefined && parsed > 0 ? parsed : undefined;
  return {
    reachOptimization:
      percentage !== undefined || (broadcast.reachOptimization ?? false),
    reachOptimizationPercentage:
      percentage ?? DEFAULT_REACH_OPTIMIZATION_PERCENTAGE,
  };
}

/**
 * Smart Audience fields for a create/draft request. When the toggle is on we
 * send only the percentage (1–99), never the bool; when off we send neither
 * (create validation requires ≥1, so `"0"` must never be sent on create).
 */
function buildReachOptimizationCreateFields(
  reachOptimization: boolean | undefined,
  reachOptimizationPercentage: number | undefined,
): { reachOptimizationPercentage?: string } {
  if (!reachOptimization) return {};
  return {
    reachOptimizationPercentage: String(
      reachOptimizationPercentage ?? DEFAULT_REACH_OPTIMIZATION_PERCENTAGE,
    ),
  };
}

export function hydrateEnforceFrequencyCap(
  broadcast: AudiencesBroadcast,
): boolean {
  return broadcast.enforceFrequencyCap ?? false;
}

/**
 * Split a broadcast's audiences into target vs. excluded ("do not send to").
 * The output-only `audiences` array carries names for both target and excluded
 * audiences; `excludedAudienceIds` identifies which of them are exclusions.
 * Any excluded id without a name in `audiences` falls back to its bare id.
 */
export function splitBroadcastAudiences(broadcast: AudiencesBroadcast): {
  target: Array<{ id: string; name: string }>;
  excluded: Array<{ id: string; name: string }>;
} {
  const audiences = broadcast.audiences ?? [];
  const excludedIds = new Set(broadcast.excludedAudienceIds ?? []);
  const target = audiences.filter((a) => !excludedIds.has(a.id));
  const namedExcluded = audiences.filter((a) => excludedIds.has(a.id));
  const namedExcludedIds = new Set(namedExcluded.map((a) => a.id));
  const excluded = [
    ...namedExcluded,
    ...[...excludedIds]
      .filter((id) => !namedExcludedIds.has(id))
      .map((id) => ({ id, name: id })),
  ];
  return { target, excluded };
}

// --- Form validation schemas ---

const sendModeSchema = z.enum(["instant", "schedule"]);

const composePrimaryChannelSchema = z
  .enum(["SMS", "MMS", "RCS", "INVALID"])
  .refine((channel) => channel !== "INVALID", {
    message: "Adjust your message for a valid channel",
  });

const broadcastV2ComposeScheduleRefine = (
  data: {
    sendMode: "instant" | "schedule";
    scheduledAtIso: string | null;
  },
  ctx: z.RefinementCtx,
) => {
  if (data.sendMode === "schedule") {
    if (!data.scheduledAtIso?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Pick a schedule date and time",
        path: ["scheduledAtIso"],
      });
    }
  }
};

const composeNameSchema = z
  .string()
  .trim()
  .min(1, "Add a name for your broadcast")
  .max(
    BROADCAST_NAME_MAX_LENGTH,
    `Name must be at most ${BROADCAST_NAME_MAX_LENGTH} characters`,
  );

const composeConfirmSendSchema = z
  .string()
  .refine((value) => value === "CONFIRM", {
    message: "Type CONFIRM to complete",
  });

const composeAudienceIdsSchema = z
  .array(z.string())
  .min(1, "Select at least one audience or list");

/** Draft save: loose validation — only name required. */
export const broadcastV2ComposeDraftFormSchema = z.object({
  name: composeNameSchema,
  audienceIds: z.array(z.string()),
  sendMode: sendModeSchema,
  scheduledAtIso: z.string().nullable(),
  primaryChannel: composePrimaryChannelSchema,
});

export type BroadcastV2ComposeDraftFormValues = z.infer<
  typeof broadcastV2ComposeDraftFormSchema
>;

/** Form + UI validation (stricter). */
export const broadcastV2ComposeFormSchema = z
  .object({
    name: composeNameSchema,
    audienceIds: composeAudienceIdsSchema,
    sendMode: sendModeSchema,
    scheduledAtIso: z.string().nullable(),
    confirmSend: composeConfirmSendSchema,
    primaryChannel: composePrimaryChannelSchema,
  })
  .superRefine(broadcastV2ComposeScheduleRefine);

export type BroadcastV2ComposeFormValues = z.infer<
  typeof broadcastV2ComposeFormSchema
>;

/**
 * Maps text-composer channel to Audiences `force_channel`.
 * The API only accepts MEDIUM_TYPE_EMAIL (1) and MEDIUM_TYPE_SMS (2); MMS/RCS are sent as SMS.
 */
export function mapTextComposerPrimaryToBroadcastForceChannel(
  channel: TextComposerPrimaryChannel,
): "MEDIUM_TYPE_SMS" | undefined {
  if (channel === "SMS" || channel === "MMS" || channel === "RCS") {
    return "MEDIUM_TYPE_SMS";
  }
  return undefined;
}

// --- Email compose form schema ---

export const broadcastV2ComposeEmailFormSchema = z
  .object({
    name: composeNameSchema,
    audienceIds: composeAudienceIdsSchema,
    fromName: z.string().trim().min(1, "From name is required"),
    fromEmail: z.string().email("Valid email is required"),
    replyTo: z.string().email("Valid email is required"),
    sendMode: sendModeSchema,
    scheduledAtIso: z.string().nullable(),
    confirmSend: composeConfirmSendSchema,
    frameId: z.string().nullable(),
  })
  .superRefine(broadcastV2ComposeScheduleRefine)
  .superRefine((data, ctx) => {
    if (!data.frameId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Select a tile from the right panel",
        path: ["frameId"],
      });
    }
  });

export type BroadcastV2ComposeEmailFormValues = z.infer<
  typeof broadcastV2ComposeEmailFormSchema
>;

const optionalEmailSchema = z
  .string()
  .trim()
  .refine((v) => v.length === 0 || z.string().email().safeParse(v).success, {
    message: "Must be a valid email or empty",
  });

/** Draft save for email: name + frame required, everything else optional. */
export const broadcastV2ComposeEmailDraftFormSchema = z.object({
  name: composeNameSchema,
  frameId: z.preprocess(
    (v) => (v == null ? "" : v),
    z.string().min(1, "Select a tile before saving"),
  ),
  audienceIds: z.array(z.string()),
  fromName: z.string().trim(),
  fromEmail: optionalEmailSchema,
  replyTo: optionalEmailSchema,
  sendMode: sendModeSchema,
  scheduledAtIso: z.string().nullable(),
});

export type BroadcastV2ComposeEmailDraftFormValues = z.infer<
  typeof broadcastV2ComposeEmailDraftFormSchema
>;

// --- Duration conversion ---

export function convertTestDurationToHours(
  value: number,
  unit: TestDurationUnit,
): number {
  return unit === "Days" ? value * 24 : value;
}

export function convertTestDurationFromHours(hours: number): {
  value: number;
  unit: TestDurationUnit;
} {
  if (hours > 0 && hours % 24 === 0) {
    return { value: hours / 24, unit: "Days" };
  }
  return { value: Math.max(1, hours), unit: "Hours" };
}

// --- Variation and A/B option builders ---

/**
 * Map the ordered variation array to wire variations. Email variations carry
 * `emailOptions`; SMS variations (no `email` payload) emit only `frameId`.
 * Variations without a frame are dropped (matches the prior A/B behavior).
 */
export function buildVariationsFromStore(
  variations: VariationDraft[],
): BroadcastVariation[] {
  return variations
    .filter((v) => v.frameId != null)
    .map((v) => {
      if (!v.email) return { frameId: v.frameId! };
      const fromName = v.email.fromName.trim();
      const fromEmail = v.email.fromEmail.trim();
      const replyTo = v.email.replyTo.trim();
      if (!fromName && !fromEmail && !replyTo) {
        return { frameId: v.frameId! };
      }
      return {
        frameId: v.frameId!,
        emailOptions: { fromName, fromEmail, replyTo },
      };
    });
}

/**
 * The A/B test option is only meaningful for multi-variation broadcasts, so it
 * is emitted only when there is more than one variation.
 */
export function buildAbTestOptionFromStore(
  variations: VariationDraft[],
  abTest: AbTestConfig,
): AbTestOption | undefined {
  if (variations.length <= 1) return undefined;
  return {
    winningMetric: winningMetricToWire(abTest.winningMetric),
    testSizePercentage: String(abTest.testSizePercent),
    testDurationHours: String(
      convertTestDurationToHours(
        abTest.testDurationValue,
        abTest.testDurationUnit,
      ),
    ),
  };
}

// --- Schedule builder ---

export function buildV2Schedule(args: {
  sendMode: SendMode;
  scheduledAtIso: string | null;
}): BroadcastSchedule | undefined {
  if (args.sendMode === "instant") return { now: true as const };
  if (args.scheduledAtIso?.trim()) {
    return { scheduledAt: args.scheduledAtIso };
  }
  return undefined;
}

// --- Create payload builders ---

export function buildCreateEmailBroadcastRequestV2(args: {
  name: string;
  audienceIds: string[];
  excludedAudienceIds?: string[];
  sendMode: SendMode;
  scheduledAtIso: string | null;
  variations: VariationDraft[];
  abTest: AbTestConfig;
  smartSend?: boolean;
  reachOptimization?: boolean;
  reachOptimizationPercentage?: number;
  enforceFrequencyCap?: boolean;
  /** Owning-system stamp ("marketing_calendar" from the plan flow). */
  source?: string;
}): CreateBroadcastRequestV2 {
  const variations = buildVariationsFromStore(args.variations);
  const abTestOption = buildAbTestOptionFromStore(args.variations, args.abTest);
  const schedule = buildV2Schedule(args);

  const payload: CreateBroadcastRequestV2 = {
    name: args.name.trim().slice(0, BROADCAST_NAME_MAX_LENGTH),
    audienceIds: args.audienceIds,
    ...(args.excludedAudienceIds?.length
      ? { excludedAudienceIds: args.excludedAudienceIds }
      : {}),
    forceChannel: "MEDIUM_TYPE_EMAIL" as const,
    ...(schedule ? { schedule } : {}),
    variations,
    ...(abTestOption ? { abTestOption } : {}),
    ...(args.smartSend ? { smartSend: true } : {}),
    ...buildReachOptimizationCreateFields(
      args.reachOptimization,
      args.reachOptimizationPercentage,
    ),
    enforceFrequencyCap: args.enforceFrequencyCap ?? false,
    ...(args.source ? { source: args.source } : {}),
  };

  const result = createBroadcastRequestV2Schema.safeParse(payload);
  if (!result.success) throw result.error;
  return result.data;
}

export function buildDraftEmailBroadcastRequestV2(args: {
  name: string;
  audienceIds: string[];
  excludedAudienceIds?: string[];
  sendMode: SendMode;
  scheduledAtIso: string | null;
  variations: VariationDraft[];
  abTest: AbTestConfig;
  smartSend?: boolean;
  reachOptimization?: boolean;
  reachOptimizationPercentage?: number;
  enforceFrequencyCap?: boolean;
  /** Owning-system stamp ("marketing_calendar" from the plan flow). */
  source?: string;
}): CreateBroadcastRequestV2 {
  const variations = buildVariationsFromStore(args.variations);
  const abTestOption = buildAbTestOptionFromStore(args.variations, args.abTest);
  const schedule = buildV2Schedule(args);

  const payload: CreateBroadcastRequestV2 = {
    name: args.name.trim().slice(0, BROADCAST_NAME_MAX_LENGTH),
    audienceIds: args.audienceIds,
    ...(args.excludedAudienceIds?.length
      ? { excludedAudienceIds: args.excludedAudienceIds }
      : {}),
    forceChannel: "MEDIUM_TYPE_EMAIL" as const,
    ...(schedule ? { schedule } : {}),
    variations,
    ...(abTestOption ? { abTestOption } : {}),
    ...(args.smartSend ? { smartSend: true } : {}),
    ...buildReachOptimizationCreateFields(
      args.reachOptimization,
      args.reachOptimizationPercentage,
    ),
    enforceFrequencyCap: args.enforceFrequencyCap ?? false,
    ...(args.source ? { source: args.source } : {}),
  };

  const result = createBroadcastRequestV2Schema.safeParse(payload);
  if (!result.success) throw result.error;
  return result.data;
}

// --- V2 A/B-aware email form validation ---

/** One email variation in the array-based form schema. */
const emailVariationFormSchema = z.object({
  frameId: z.string().nullable(),
  fromName: z.string().trim(),
  fromEmail: z.string(),
  replyTo: z.string(),
});

export const broadcastV2ComposeEmailFormSchemaV2 = z
  .object({
    name: composeNameSchema,
    audienceIds: composeAudienceIdsSchema,
    sendMode: sendModeSchema,
    scheduledAtIso: z.string().nullable(),
    confirmSend: composeConfirmSendSchema,
    variations: z.array(emailVariationFormSchema).min(1).max(MAX_VARIATIONS),
  })
  .superRefine(broadcastV2ComposeScheduleRefine)
  .superRefine((data, ctx) => {
    const isAb = data.variations.length > 1;
    data.variations.forEach((v, i) => {
      const label = isAb ? `${variationLabel(i)}: ` : "";
      if (!v.frameId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: isAb
            ? `${variationLabel(i)} needs a tile`
            : "Select a tile from the right panel",
          path: ["variations", i, "frameId"],
        });
      }
      if (!v.fromName.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${label}From name is required`,
          path: ["variations", i, "fromName"],
        });
      }
      if (!z.string().email().safeParse(v.fromEmail).success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${label}Valid email is required`,
          path: ["variations", i, "fromEmail"],
        });
      }
      if (!z.string().email().safeParse(v.replyTo).success) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `${label}Valid reply-to is required`,
          path: ["variations", i, "replyTo"],
        });
      }
    });
  });

export type BroadcastV2ComposeEmailFormValuesV2 = z.infer<
  typeof broadcastV2ComposeEmailFormSchemaV2
>;

/** Draft save for email V2: name required, at least one variation with a frameId. */
export const broadcastV2ComposeEmailDraftFormSchemaV2 = z
  .object({
    name: composeNameSchema,
    audienceIds: z.array(z.string()),
    sendMode: sendModeSchema,
    scheduledAtIso: z.string().nullable(),
    variations: z
      .array(z.object({ frameId: z.string().nullable() }))
      .min(1)
      .max(MAX_VARIATIONS),
  })
  .superRefine((data, ctx) => {
    // Every variation needs a tile: frameless variations are silently dropped
    // by buildVariationsFromStore, so a saved draft loses that tab on refresh.
    const isAb = data.variations.length > 1;
    data.variations.forEach((v, i) => {
      if (!v.frameId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: isAb
            ? `${variationLabel(i)} needs a tile — pick one or remove the variation`
            : "Select a tile before saving",
          path: ["variations", i, "frameId"],
        });
      }
    });
  });

export type BroadcastV2ComposeEmailDraftFormValuesV2 = z.infer<
  typeof broadcastV2ComposeEmailDraftFormSchemaV2
>;

// --- V2 SMS builders ---

interface SmsBroadcastRequestArgs {
  name: string;
  audienceIds: string[];
  excludedAudienceIds?: string[];
  sendMode: SendMode;
  scheduledAtIso: string | null;
  primaryChannel: TextComposerPrimaryChannel;
  /**
   * Ordered variations with their (just-created) frame ids populated. SMS
   * variations carry no `email` payload, so each emits only `frameId`.
   */
  variations: VariationDraft[];
  abTest: AbTestConfig;
  smartSend?: boolean;
  reachOptimization?: boolean;
  reachOptimizationPercentage?: number;
  enforceFrequencyCap?: boolean;
  /** Owning-system stamp ("marketing_calendar" from the plan flow). */
  source?: string;
}

/**
 * Create/draft SMS broadcasts share an identical body — the only SMS-specific
 * bit vs email is `forceChannel`. Both reuse the shared variation + A/B-option
 * builders so the metric/duration → wire conversion lives in exactly one place.
 */
function buildSmsBroadcastRequestV2(
  args: SmsBroadcastRequestArgs,
): CreateBroadcastRequestV2 {
  const forceChannel = mapTextComposerPrimaryToBroadcastForceChannel(
    args.primaryChannel,
  );
  const schedule = buildV2Schedule(args);
  const variations = buildVariationsFromStore(args.variations);
  const abTestOption = buildAbTestOptionFromStore(args.variations, args.abTest);

  const payload: CreateBroadcastRequestV2 = {
    name: args.name.trim().slice(0, BROADCAST_NAME_MAX_LENGTH),
    audienceIds: args.audienceIds,
    ...(args.excludedAudienceIds?.length
      ? { excludedAudienceIds: args.excludedAudienceIds }
      : {}),
    ...(forceChannel !== undefined ? { forceChannel } : {}),
    ...(schedule ? { schedule } : {}),
    variations,
    ...(abTestOption ? { abTestOption } : {}),
    ...(args.smartSend ? { smartSend: true } : {}),
    ...buildReachOptimizationCreateFields(
      args.reachOptimization,
      args.reachOptimizationPercentage,
    ),
    enforceFrequencyCap: args.enforceFrequencyCap ?? false,
    ...(args.source ? { source: args.source } : {}),
  };

  const result = createBroadcastRequestV2Schema.safeParse(payload);
  if (!result.success) throw result.error;
  return result.data;
}

export function buildCreateSmsBroadcastRequestV2(
  args: SmsBroadcastRequestArgs,
): CreateBroadcastRequestV2 {
  return buildSmsBroadcastRequestV2(args);
}

export function buildDraftSmsBroadcastRequestV2(
  args: SmsBroadcastRequestArgs,
): CreateBroadcastRequestV2 {
  return buildSmsBroadcastRequestV2(args);
}

// --- Update payload builder (differential) ---

export function buildUpdateBroadcastPayload(args: {
  name: string;
  originalBroadcast: AudiencesBroadcast;
  audienceIds: string[];
  excludedAudienceIds?: string[];
  variations: BroadcastVariation[];
  abTestOption?: AbTestOption;
  schedule?: BroadcastSchedule;
  smartSend?: boolean;
  reachOptimization?: boolean;
  reachOptimizationPercentage?: number;
  enforceFrequencyCap?: boolean;
}): UpdateBroadcastRequestV2 {
  const audienceDiff = diffAudienceIds(
    args.originalBroadcast.audienceIds ?? [],
    args.audienceIds,
  );
  const excludedDiff = diffAudienceIds(
    args.originalBroadcast.excludedAudienceIds ?? [],
    args.excludedAudienceIds ?? [],
  );
  const variationDiff = diffVariations(
    args.originalBroadcast.variations ?? [],
    args.variations,
  );

  const smartSendChanged =
    args.smartSend !== undefined &&
    args.smartSend !== (args.originalBroadcast.smartSend ?? false);

  const enforceFrequencyCapChanged =
    args.enforceFrequencyCap !== undefined &&
    args.enforceFrequencyCap !==
      hydrateEnforceFrequencyCap(args.originalBroadcast);

  // Reach optimization (Smart Audience): the toggle (`reachOptimization` bool)
  // plus a percentage, sent independently per the backend contract. On the wire
  // the percentage is an int64-as-string where "0" clears it.
  //   ON  → send the percentage when it differs from the persisted one (or none
  //         was persisted); never send the bool.
  //   OFF → send "0" when a percentage was persisted, and `false` when the bool
  //         was persisted true, so any prior state (incl. a legacy bool-only
  //         broadcast) is reliably turned off.
  const persistedReachPercentage = args.originalBroadcast
    .reachOptimizationPercentage
    ? Number(args.originalBroadcast.reachOptimizationPercentage)
    : undefined;
  const persistedReachBool = args.originalBroadcast.reachOptimization ?? false;
  const reachOptimizationUpdate: {
    reachOptimization?: boolean;
    reachOptimizationPercentage?: string;
  } = {};
  if (args.reachOptimization === true) {
    const next =
      args.reachOptimizationPercentage ?? DEFAULT_REACH_OPTIMIZATION_PERCENTAGE;
    if (persistedReachPercentage !== next) {
      // The builder takes an unrestricted number and the update payload is sent
      // unparsed, so validate the 1–99 range here — the composer-store clamp
      // only protects the normal UI path, not direct callers. Throws on
      // out-of-range/fractional input.
      reachOptimizationUpdate.reachOptimizationPercentage =
        createReachOptimizationPercentageSchema.parse(String(next));
    }
  } else if (args.reachOptimization === false) {
    if (persistedReachPercentage !== undefined) {
      reachOptimizationUpdate.reachOptimizationPercentage = "0";
    }
    if (persistedReachBool) {
      reachOptimizationUpdate.reachOptimization = false;
    }
  }

  return {
    name: args.name,
    ...(args.abTestOption ? { abTestOption: args.abTestOption } : {}),
    ...(variationDiff.update.length > 0
      ? { updateVariations: variationDiff.update }
      : {}),
    ...(variationDiff.add.length > 0
      ? { addVariations: variationDiff.add }
      : {}),
    ...(variationDiff.remove.length > 0
      ? { removeVariations: variationDiff.remove }
      : {}),
    ...(args.schedule ? { schedule: args.schedule } : {}),
    ...(audienceDiff.toAdd.length > 0
      ? { addAudienceIds: audienceDiff.toAdd }
      : {}),
    ...(audienceDiff.toRemove.length > 0
      ? { removeAudienceIds: audienceDiff.toRemove }
      : {}),
    ...(excludedDiff.toAdd.length > 0
      ? { addExcludedAudienceIds: excludedDiff.toAdd }
      : {}),
    ...(excludedDiff.toRemove.length > 0
      ? { removeExcludedAudienceIds: excludedDiff.toRemove }
      : {}),
    ...(smartSendChanged ? { smartSend: args.smartSend } : {}),
    ...reachOptimizationUpdate,
    ...(enforceFrequencyCapChanged
      ? { enforceFrequencyCap: args.enforceFrequencyCap }
      : {}),
  };
}

// --- Frame payload builder ---

export function buildCreateBroadcastFramePayload(args: {
  siteId: string;
  frameName: string;
  innerTileId: string;
  frameOutput: TextComposerFramePreviewOutput;
  productModel?: ProductGridModelValue;
  selectedEventId?: string;
}): CreateFramePayloadParsed {
  const variants = buildTextComposerFrameVariantWireRows({
    frameOutput: args.frameOutput,
    productModel: args.productModel,
  });

  return createFramePayload({
    siteId: args.siteId,
    frameName: args.frameName,
    innerTileId: args.innerTileId,
    variants,
    metadata: createFrameMetadata({
      selectedEventId: args.selectedEventId,
    }),
  });
}

// --- SMS compose form schema ---

export const broadcastV2ComposeSmsFormSchema = broadcastV2ComposeFormSchema;
export type BroadcastV2ComposeSmsFormValues = BroadcastV2ComposeFormValues;

export const broadcastV2ComposeSmsDraftFormSchema =
  broadcastV2ComposeDraftFormSchema;
export type BroadcastV2ComposeSmsDraftFormValues =
  BroadcastV2ComposeDraftFormValues;
